import React, { useState, useEffect } from 'react';
import './ProductsManager.css';
import ImageInput from './ImageInput';

const API_URL = process.env.REACT_APP_API_URL || '';

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    image: '',
    images: [],
    published: false,
    featured: false,
    stock: 0,
    sku: '',
    collectionId: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCollections();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/products/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load products' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/collections/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setCollections(data);
    } catch (error) {
      console.error('Failed to load collections:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    
    // Auto-generate slug from name
    if (e.target.name === 'name' && !editing) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleMultipleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('accessToken');
      const formDataUpload = new FormData();
      files.forEach(file => {
        formDataUpload.append('images', file);
      });

      const response = await fetch(`${API_URL}/api/upload/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      const fullUrls = data.urls.map(url => `${API_URL}${url}`);
      setFormData(prev => ({ ...prev, images: [...prev.images, ...fullUrls] }));
      setMessage({ type: 'success', text: `${data.count} image(s) uploaded successfully!` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload images' });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('accessToken');
    
    try {
      const url = editing 
        ? `${API_URL}/api/products/${editing}`
        : `${API_URL}/api/products`;
      
      const payload = {
        ...formData,
        collectionId: formData.collectionId ? parseInt(formData.collectionId) : null
      };
      
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      setMessage({ type: 'success', text: editing ? 'Product updated!' : 'Product created!' });
      resetForm();
      fetchProducts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save product' });
    }
  };

  const handleEdit = (product) => {
    setFormData({
      ...product,
      images: product.images || [],
      collectionId: product.collectionId || ''
    });
    setEditing(product.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      setMessage({ type: 'success', text: 'Product deleted!' });
      fetchProducts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete product' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      image: '',
      images: [],
      published: false,
      featured: false,
      stock: 0,
      sku: '',
      collectionId: ''
    });
    setEditing(null);
    setShowForm(false);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="products-manager">
      <div className="manager-header">
        <h2>Manage Products</h2>
        <button className="add-button" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Product'}
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Slug *</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price *</label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Stock</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>SKU</label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Collection</label>
              <select
                name="collectionId"
                value={formData.collectionId}
                onChange={handleChange}
              >
                <option value="">No Collection</option>
                {collections.map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <ImageInput label="Image" value={formData.image} onChange={(v) => setFormData(prev => ({ ...prev, image: v }))} />
          </div>

          <div className="form-group">
            <label>Additional Images (Gallery)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleMultipleImagesUpload}
              disabled={uploading}
            />
            {uploading && <span className="upload-status">Uploading...</span>}
            {formData.images && formData.images.length > 0 && (
              <div className="images-gallery">
                {formData.images.map((img, index) => (
                  <div key={index} className="gallery-image">
                    <img src={img} alt={`Gallery ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-row checkbox-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="published"
                  checked={formData.published}
                  onChange={handleChange}
                />
                <span>Published</span>
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />
                <span>Featured</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={resetForm} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="save-button">
              {editing ? 'Update' : 'Create'} Product
            </button>
          </div>
        </form>
      )}

      <div className="products-list">
        {products.length === 0 ? (
          <p className="empty-state">No products yet. Create your first one!</p>
        ) : (
          products.map(product => (
            <div key={product.id} className="product-card">
              {product.image && (
                <div className="product-image">
                  <img src={product.image} alt={product.name} />
                </div>
              )}
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-slug">{product.slug}</p>
                {product.description && (
                  <p className="product-description">{product.description}</p>
                )}
                <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
                <div className="product-meta">
                  <span className={`status ${product.published ? 'published' : 'draft'}`}>
                    {product.published ? 'Published' : 'Draft'}
                  </span>
                  {product.featured && (
                    <span className="badge featured">Featured</span>
                  )}
                  {product.collection && (
                    <span className="badge collection">{product.collection.name}</span>
                  )}
                  <span className="stock">Stock: {product.stock}</span>
                </div>
              </div>
              <div className="product-actions">
                <button onClick={() => handleEdit(product)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => handleDelete(product.id)} className="delete-btn">
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductsManager;
